import { ArrowLeft, Copy, ExternalLink, FileText, Pencil, Power, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SectionCard } from "~/components/section-card";
import { Badge, Button } from "~/components/ui";
import type { ReferenceDetailUI } from "~/features/references/types";
import { cn } from "~/lib/utils";

interface ReferenceDetailPresenterProps {
  detail: ReferenceDetailUI;
  variant: "page" | "peek";
  canDeactivate: boolean;
  onBack: () => void;
  onCopyId: (id: string) => void;
  onEdit: () => void;
  onRequestToggleActivation: () => void;
  onAddFile: () => void;
  onReplaceFile: () => void;
  onEditFile: () => void;
  onViewFile: (referenceFileId: string) => void;
}

export function ReferenceDetailPresenter({
  detail,
  variant,
  canDeactivate,
  onBack,
  onCopyId,
  onEdit,
  onRequestToggleActivation,
  onAddFile,
  onReplaceFile,
  onEditFile,
  onViewFile,
}: ReferenceDetailPresenterProps) {
  const { t } = useTranslation();
  const isPeek = variant === "peek";

  return (
    <div className={cn("space-y-5", isPeek ? "p-1" : "mx-auto max-w-[var(--content-max)] p-8")}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          {!isPeek && (
            <Button type="button" variant="ghost" size="sm" className="px-0 text-fg-2" onClick={onBack}>
              <ArrowLeft />
              <span>{t("ui.back")}</span>
            </Button>
          )}
          <div className="flex items-center gap-2">
            <h1 className={cn("text-fg-1", isPeek ? "text-16-bold" : "text-dewey-title")}>{detail.title}</h1>
            {detail.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
          </div>
          <div className="flex items-center gap-2 text-12-regular text-fg-3">
            <span className="tabular-nums">{detail.id}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              aria-label={t("ui.copyId")}
              onClick={() => onCopyId(detail.id)}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
          {detail.isDeactivated ? (
            <Button type="button" variant="ghost" size="sm" onClick={onRequestToggleActivation}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            canDeactivate && (
              <Button type="button" variant="ghost" size="sm" onClick={onRequestToggleActivation}>
                <PowerOff />
                <span>{t("ui.deactivate")}</span>
              </Button>
            )
          )}
        </div>
      </div>

      <SectionCard
        icon={FileText}
        title={t("model.reference.modelName")}
        contentClassName="divide-y divide-dashed divide-border-default py-0 text-[13px]"
      >
        <Row label={t("model.reference.seqID")}>
          <span className="tabular-nums text-fg-1">{detail.seqId}</span>
        </Row>
        <Row label={t("model.reference.title")}>{detail.title}</Row>
        <Row label={t("model.reference.shortCode")}>{detail.shortCode}</Row>
        <Row label={t("model.reference.isbn")}>{detail.isbn || "—"}</Row>
        <Row label={t("model.reference.authors")}>{detail.authors.length > 0 ? detail.authors.join(", ") : "—"}</Row>
        <Row label={t("model.reference.publishers")}>{detail.publishers.join(", ") || "—"}</Row>
        <Row label={t("model.reference.publishedDate")}>{detail.publishedDate || "—"}</Row>
        <Row label={t("model.reference.edition")}>{detail.edition || "—"}</Row>
        <Row label={t("model.dataImportProject.document")}>
          {detail.documentUrl ? (
            <a
              href={detail.documentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 break-all text-fg-link hover:underline"
            >
              {detail.documentUrl}
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
          ) : (
            "—"
          )}
        </Row>
        <Row label={t("model.evidence.modelNames")}>
          <span className="tabular-nums text-fg-1">{detail.evidencesCount}</span>
        </Row>
      </SectionCard>

      <SectionCard icon={FileText} title={t("model.referenceFile.modelName")} contentClassName="text-[13px]">
        {detail.file ? (
          <div className="space-y-3">
            <Row label={t("model.common.id")}>
              <span className="tabular-nums text-fg-1">{detail.file.id}</span>
            </Row>
            <Row label={t("model.referenceFile.googleDriveFileId")}>
              <a
                href={`https://drive.google.com/file/d/${detail.file.googleDriveFileId}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 break-all text-fg-link hover:underline"
              >
                {detail.file.googleDriveFileId}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            </Row>
            <Row label={t("model.referenceFile.folioOffset")}>
              <span className="tabular-nums text-fg-1">{detail.file.folioOffset}</span>
            </Row>
            <div className="flex flex-wrap gap-2 pt-1">
              {detail.file.hasImages && detail.file.id && (
                <Button type="button" variant="outline" size="sm" onClick={() => onViewFile(detail.file?.id ?? "")}>
                  {t("ui.view")}
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" onClick={onEditFile}>
                <Pencil />
                <span>{t("ui.edit")}</span>
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={onReplaceFile}>
                {t("ui.replace", { defaultValue: "Replace" })}
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={onAddFile}>
            {t("ui.add")} {t("model.referenceFile.modelName")}
          </Button>
        )}
      </SectionCard>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(6rem,9rem)_1fr] items-start gap-4 py-2.5">
      <span className="text-fg-3">{label}</span>
      <div className="min-w-0 text-fg-1">{children}</div>
    </div>
  );
}
